import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BookOpen, Check, Star, Trash2, Plus, LogOut, ChevronLeft, ChevronRight, Mail, Loader2, ChevronDown, Columns2, Trophy, HelpCircle, Linkedin, Menu, X, PanelLeftClose, PanelLeftOpen, ListChecks } from "lucide-react";
import { useImages, useSite } from "@/context/SiteContent";
import { TikTokIcon, LinkedInIcon } from "@/components/site/Scott";
import { readerApi, readerToken, formatApiErrorDetail, API } from "@/lib/readerApi";
const uid = () => Math.random().toString(36).slice(2, 9);

/* ---------------- Auth screen ---------------- */
function ReaderAuth({ onAuthed, resetToken }) {
  const images = useImages();
  const [mode, setMode] = useState(resetToken ? "reset" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "login") { const d = await readerApi.login(form.email, form.password); readerToken.set(d.token); onAuthed(d); }
      else if (mode === "register") { const d = await readerApi.register(form); readerToken.set(d.token); onAuthed(d); }
      else if (mode === "forgot") { await readerApi.forgot(form.email); setSent(true); }
      else if (mode === "reset") { await readerApi.reset(resetToken, form.password); toast.success("Password updated. Please sign in."); setMode("login"); }
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="reader-auth" data-testid="reader-auth">
      <div className="reader-auth-card">
        <Link to="/" className="reader-auth-logo"><img alt="TransitionWorks" src="/tw-logo.png" /></Link>
        {images.cover && <img className="reader-auth-cover" src={images.cover} alt="From Campus to Corporate cover" data-testid="reader-auth-cover" />}
        <span className="eyebrow">Your interactive eBook</span>
        <h1>{mode === "register" ? "Create your reader account" : mode === "forgot" ? "Reset your password" : mode === "reset" ? "Set a new password" : "Sign in to read"}</h1>
        {mode === "register" && <p className="reader-auth-note">Use the same email you paid with. Your eBook, notes and progress live in your account.</p>}
        {mode === "login" && <p className="reader-auth-note">From Campus to Corporate reads online only, so it stays yours and unshareable.</p>}

        {sent ? (
          <p className="reader-auth-note" data-testid="reader-forgot-sent">If an account exists for that email, a reset link is on its way. Check your inbox.</p>
        ) : (
          <form onSubmit={submit} className="reader-auth-form">
            {mode === "register" && <input data-testid="reader-name-input" placeholder="Your name (optional)" value={form.name} onChange={set("name")} />}
            {mode !== "reset" && <input data-testid="reader-email-input" type="email" required placeholder="Email address" value={form.email} onChange={set("email")} />}
            {mode !== "forgot" && <input data-testid="reader-password-input" type="password" required placeholder={mode === "reset" ? "New password" : "Password"} value={form.password} onChange={set("password")} />}
            {err && <div className="reader-auth-err" data-testid="reader-auth-error">{err}</div>}
            <button className="btn btn-gold btn-block" disabled={busy} data-testid="reader-auth-submit" type="submit">
              {busy ? "Please wait..." : mode === "register" ? "Create account & read" : mode === "forgot" ? "Send reset link" : mode === "reset" ? "Update password" : "Sign in"}
            </button>
          </form>
        )}

        <div className="reader-auth-switch">
          {mode === "login" && <>
            <button type="button" onClick={() => { setErr(""); setMode("register"); }} data-testid="reader-switch-register">First time here? Create your account</button>
            <button type="button" className="link-muted" onClick={() => { setErr(""); setSent(false); setMode("forgot"); }} data-testid="reader-switch-forgot">Forgot password?</button>
          </>}
          {mode === "register" && <button type="button" onClick={() => { setErr(""); setMode("login"); }} data-testid="reader-switch-login">Already have an account? Sign in</button>}
          {(mode === "forgot" || mode === "reset") && <button type="button" onClick={() => { setErr(""); setSent(false); setMode("login"); }}>Back to sign in</button>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Self-loading protected page image ---------------- */
const pageCache = new Map(); // n -> objectURL (session-scoped; pages are identical for all readers)
async function fetchPageURL(n) {
  if (pageCache.has(n)) return pageCache.get(n);
  const res = await fetch(`${API}/reader/book/page/${n}`, { headers: { Authorization: `Bearer ${readerToken.get()}` } });
  if (!res.ok) { const err = new Error("page"); err.status = res.status; throw err; }
  const url = URL.createObjectURL(await res.blob());
  pageCache.set(n, url);
  return url;
}
function prefetchPage(n, total) {
  if (n < 0 || (total && n >= total) || pageCache.has(n)) return;
  fetchPageURL(n).catch(() => {});
}

function BookPage({ n, onAuthErr, testId, overlay }) {
  const [url, setUrl] = useState(() => pageCache.get(n) || null);
  const [loading, setLoading] = useState(() => !pageCache.has(n));
  useEffect(() => {
    let cancelled = false;
    const cached = pageCache.get(n);
    if (cached) { setUrl(cached); setLoading(false); return; }
    setLoading(true);
    (async () => {
      try {
        const u = await fetchPageURL(n);
        if (!cancelled) { setUrl(u); }
      } catch (e) { if (e?.status) onAuthErr({ status: e.status }); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n]);
  return (
    <div className="reader-page-frame" data-testid="reader-page-frame">
      {loading && <div className="reader-page-loading"><Loader2 className="spin" size={26} /></div>}
      {url && <img src={url} alt={`Page ${n + 1}`} className="reader-page-img" onContextMenu={(e) => e.preventDefault()} draggable={false} data-testid={testId} />}
      {url && overlay}
    </div>
  );
}

/* ---------------- On-page fill-in fields (works on any page with printed boxes) ---------------- */
function FieldsOverlay({ fields, value, onChange, keyId }) {
  const pct = (v) => `${v * 100}%`;
  const boxes = fields.boxes || [];
  const checks = fields.checks || [];
  const boxVals = value.boxes || {};
  const checkVals = value.checks || {};
  return (
    <div className="reader-ws-overlay" data-testid={`ws-overlay-${keyId}`}>
      {boxes.map((b, i) => (
        <textarea key={`b${i}`} className="reader-ws-input"
          style={{ position: "absolute", left: pct(b[0]), top: pct(b[1]), width: pct(b[2]), height: pct(b[3]) }}
          value={boxVals[i] || ""} placeholder="Write here…"
          onChange={(e) => onChange({ boxes: { ...boxVals, [i]: e.target.value } })}
          data-testid={`ws-box-${keyId}-${i}`} />
      ))}
      {checks.map((c, j) => (
        <button key={`c${j}`} type="button" className={`reader-ws-check${checkVals[j] ? " is-on" : ""}`}
          style={{ position: "absolute", left: pct(c[0] - 0.001), top: pct(c[1] - 0.004), width: pct(c[2] + 0.004), height: pct(c[3] + 0.01) }}
          onClick={() => onChange({ checks: { ...checkVals, [j]: !checkVals[j] } }, true)}
          aria-pressed={!!checkVals[j]} aria-label="Tick"
          data-testid={`ws-check-${keyId}-${j}`}>
          {checkVals[j] && <Check strokeWidth={4} />}
        </button>
      ))}
    </div>
  );
}

/* ---------------- Clickable PDF links kept alive over the page image ---------------- */
function LinksOverlay({ links, keyId, onGoto }) {
  const pct = (v) => `${v * 100}%`;
  return (
    <div className="reader-links-overlay" data-testid={`links-overlay-${keyId}`}>
      {links.map((l, i) => {
        const style = { left: pct(l.r[0]), top: pct(l.r[1]), width: pct(l.r[2]), height: pct(l.r[3]) };
        return l.uri
          ? <a key={i} className="reader-link-hot" style={style} href={l.uri} target="_blank" rel="noopener noreferrer" title={l.uri} aria-label={l.uri} data-testid={`page-link-${keyId}-${i}`} />
          : <button key={i} type="button" className="reader-link-hot" style={style} onClick={() => onGoto(l.page)} title={`Go to page ${l.page + 1}`} aria-label={`Go to page ${l.page + 1}`} data-testid={`page-link-${keyId}-${i}`} />;
      })}
    </div>
  );
}

/* ---------------- Section progress ring ---------------- */
function SectionRing({ label, pct, onClick, testId }) {
  const r = 15, c = 2 * Math.PI * r;
  return (
    <button className="reader-ring" onClick={onClick} title={`${label} · ${pct}%`} data-testid={testId}>
      <svg viewBox="0 0 40 40" width="40" height="40">
        <circle cx="20" cy="20" r={r} fill="none" stroke="rgba(255,255,255,.14)" strokeWidth="4" />
        <circle cx="20" cy="20" r={r} fill="none" stroke="var(--gold)" strokeWidth="4" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100} transform="rotate(-90 20 20)" />
        <text x="20" y="24" textAnchor="middle" fontSize="13" fontWeight="800" fill="#EBE3D6">{label[0]}</text>
      </svg>
      <em className="reader-ring-label">{label}</em>
      <small>{pct}%</small>
    </button>
  );
}

/* ---------------- First-time coach-mark tour ---------------- */
const TOUR_KEY = "tw_reader_tour_done_v1";
const TOUR_STEPS = [
  { sel: '[data-testid="reader-section-rings"]', title: "Track the 5 Ps", body: "Each ring shows how far you've read a pillar. Tap one to jump straight there." },
  { sel: '[data-testid="reader-bookmark"]', title: "Bookmark anything", body: "Star a page to save it, then find it fast under Bookmarks." },
  { sel: '[data-testid="reader-notes-panel"]', title: "Action items", body: "Keep a to-do list for any page here — and write directly in the boxes and checklists printed on the pages. Everything saves automatically." },
];

function ReaderTour({ onDone }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const onScreen = (el) => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.right > 4 && r.left < window.innerWidth - 4 && r.bottom > 4 && r.top < window.innerHeight - 4;
  };
  const steps = TOUR_STEPS.filter((s) => { const el = document.querySelector(s.sel); return el && onScreen(el); });
  useEffect(() => {
    const el = steps[i] && document.querySelector(steps[i].sel);
    if (!el) { onDone(); return; }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const t = setTimeout(() => setRect(el.getBoundingClientRect()), 260);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);
  if (!steps.length || !rect) return steps.length ? null : null;
  const step = steps[i];
  const pad = 8;
  const isMobile = window.innerWidth <= 760;
  const cardStyle = isMobile
    ? { left: 12, right: 12, bottom: 16, top: "auto", width: "auto" }
    : (() => {
        const below = rect.bottom + 200 < window.innerHeight;
        let top = below ? rect.bottom + pad + 10 : rect.top - 190;
        top = Math.min(Math.max(12, top), window.innerHeight - 210);
        const left = Math.min(Math.max(12, rect.left), window.innerWidth - 312);
        return { top, left };
      })();
  return (
    <div className="reader-tour" data-testid="reader-tour">
      <div className="reader-tour-ring" style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }} />
      <div className="reader-tour-card" style={cardStyle}>
        <span className="reader-tour-step">{i + 1} / {steps.length}</span>
        <h4>{step.title}</h4>
        <p>{step.body}</p>
        <div className="reader-tour-actions">
          <button className="reader-tour-skip" onClick={onDone} data-testid="tour-skip">Skip</button>
          <button className="btn btn-gold" onClick={() => (i + 1 < steps.length ? setI(i + 1) : onDone())} data-testid="tour-next">{i + 1 < steps.length ? "Next" : "Got it"}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Book reader ---------------- */
function ReaderApp({ user, onSignedOut }) {
  const images = useImages();
  const { links } = useSite();
  const navigate = useNavigate();
  const [pages, setPages] = useState(0);
  const [chapters, setChapters] = useState([]);
  const [progress, setProgress] = useState({});
  const [idx, setIdx] = useState(user.lastLesson || 0);
  const [newItem, setNewItem] = useState("");
  const [exporting, setExporting] = useState(false);
  const [onlyBookmarks, setOnlyBookmarks] = useState(false);
  const [openGroup, setOpenGroup] = useState(0);
  const [spread, setSpread] = useState(false);
  const [pageFields, setPageFields] = useState({});
  const [pageLinks, setPageLinks] = useState({});
  const [dir, setDir] = useState(1);
  const [celebrate, setCelebrate] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [sideOpen, setSideOpen] = useState(false);
  const [sideCollapsed, setSideCollapsed] = useState(() => localStorage.getItem("tw_reader_side_collapsed") === "1");
  const toggleCollapse = () => setSideCollapsed((v) => { localStorage.setItem("tw_reader_side_collapsed", v ? "0" : "1"); return !v; });
  const [notesOpen, setNotesOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(() => localStorage.getItem("tw_reader_panel_open") !== "0");
  const togglePanel = () => setPanelOpen((v) => { localStorage.setItem("tw_reader_panel_open", v ? "0" : "1"); return !v; });
  const timers = useRef({});
  const pending = useRef({});

  const handleErr = (e) => {
    const status = e?.response?.status || e?.status;
    if (status === 401 || status === 409) {
      toast.error(status === 409 ? "You signed in on another device." : "Please sign in again.");
      readerToken.clear(); onSignedOut();
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [info, state] = await Promise.all([readerApi.bookInfo(), readerApi.getState()]);
        setPages(info.pages || 0);
        setChapters(info.chapters || []);
        setPageFields(info.fields || {});
        setPageLinks(info.links || {});
        setProgress(state.lessons || {});
        const start = typeof state.lastLesson === "number" ? state.lastLesson : (info.lastLesson || 0);
        setIdx(Math.min(Math.max(0, start), Math.max(0, (info.pages || 1) - 1)));
        if (!localStorage.getItem(TOUR_KEY)) setTimeout(() => setShowTour(true), 900);
      } catch (e) { handleErr(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cur = String(idx);
  const ls = progress[cur] || {};

  const flush = async (key) => {
    const patch = pending.current[key];
    if (!patch) return;
    pending.current[key] = null;
    try { await readerApi.saveLesson(key, patch); } catch (e) { handleErr(e); }
  };
  const updateFor = (key, patch, immediate = false) => {
    setProgress((p) => ({ ...p, [key]: { ...(p[key] || {}), ...patch } }));
    pending.current[key] = { ...(pending.current[key] || {}), ...patch };
    if (timers.current[key]) clearTimeout(timers.current[key]);
    if (immediate) flush(key); else timers.current[key] = setTimeout(() => flush(key), 700);
  };
  const update = (patch, immediate = false) => updateFor(cur, patch, immediate);

  const goto = (n) => {
    const next = Math.max(0, Math.min(pages - 1, n));
    setDir(next >= idx ? 1 : -1);
    setIdx(next);
    setSideOpen(false);
    readerApi.setLastLesson(next).catch(handleErr);
    document.querySelector(".reader-book-stage")?.scrollTo({ top: 0, behavior: "smooth" });
  };
  const step = spread ? 2 : 1;
  const overlayFor = (n) => {
    const k = String(n);
    const fields = pageFields[k], links = pageLinks[k];
    if (!fields && !links) return null;
    return <>
      {fields && <FieldsOverlay keyId={n} fields={fields} value={progress[k] || {}} onChange={(patch, imm) => updateFor(k, patch, imm)} />}
      {links && <LinksOverlay keyId={n} links={links} onGoto={goto} />}
    </>;
  };

  useEffect(() => {
    if (!pages) return;
    [idx + 1, idx + 2, idx + 3, idx - 1].forEach((n) => prefetchPage(n, pages));
  }, [idx, pages]);

  const checklist = ls.checklist || [];
  const addItem = () => { const t = newItem.trim(); if (!t) return; update({ checklist: [...checklist, { id: uid(), text: t, checked: false }] }, true); setNewItem(""); };
  const toggleItem = (id) => update({ checklist: checklist.map((c) => c.id === id ? { ...c, checked: !c.checked } : c) }, true);
  const delItem = (id) => update({ checklist: checklist.filter((c) => c.id !== id) }, true);

  const doneCount = useMemo(() => Object.values(progress).filter((x) => x && x.done).length, [progress]);
  const pct = pages ? Math.round((doneCount / pages) * 100) : 0;

  const toggleDone = () => {
    const willBeDone = !ls.done;
    update({ done: willBeDone }, true);
    if (willBeDone && pages > 0 && doneCount + 1 >= pages) setCelebrate(true);
  };

  const goReview = async () => {
    try { const r = await readerApi.reviewLink(); setCelebrate(false); navigate(r.url); }
    catch (e) { toast.error("Could not open the review page. Please try again."); }
  };

  const getBadge = async () => {
    try {
      const res = await fetch(`${API}/reader/finish-badge`, { headers: { Authorization: `Bearer ${readerToken.get()}` } });
      if (!res.ok) { handleErr({ status: res.status }); return; }
      const blob = await res.blob();
      const file = new File([blob], "from-campus-to-corporate-finished.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "I finished From Campus to Corporate", text: "I just finished From Campus to Corporate by Scott Tang!" });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = file.name; a.click();
        URL.revokeObjectURL(url);
        toast.success("Your badge has been downloaded.");
      }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Could not create your badge."); }
  };
  const shareLinkedIn = () => {
    const url = "https://www.linkedin.com/sharing/share-offsite/?url=" + encodeURIComponent("https://thetransitionworks.com");
    window.open(url, "_blank", "noopener,noreferrer,width=680,height=640");
  };
  const bookmarks = useMemo(() => Object.keys(progress).filter((k) => progress[k]?.bookmarked).map(Number).sort((a, b) => a - b), [progress]);

  const sections = useMemo(() => {
    const PS = ["Purpose", "People", "Program", "Place", "Profit"];
    return chapters.map((g, gi) => {
      if (!PS.includes(g.p)) return null;
      const start = g.items?.[0]?.page ?? 0;
      const nextStart = gi + 1 < chapters.length ? (chapters[gi + 1].items?.[0]?.page ?? pages) : pages;
      const end = Math.max(start, nextStart - 1);
      let total = 0, done = 0;
      for (let p = start; p <= end; p++) { total++; if (progress[String(p)]?.done) done++; }
      return { p: g.p, start, done, total, pct: total ? Math.round((done / total) * 100) : 0 };
    }).filter(Boolean);
  }, [chapters, progress, pages]);

  // arrow-key page flip (ignore while typing)
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (e.key === "ArrowRight") { e.preventDefault(); goto(idx + step); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); goto(idx - step); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, step, pages]);

  // auto-open the chapter containing the current page
  const activeGroup = useMemo(() => {
    let found = 0;
    chapters.forEach((g, gi) => { const min = g.items?.[0]?.page ?? 0; if (min <= idx) found = gi; });
    return found;
  }, [chapters, idx]);
  useEffect(() => { setOpenGroup(activeGroup); }, [activeGroup]);

  const exportNotes = async () => {
    setExporting(true);
    await Promise.all(Object.keys(pending.current).map((k) => flush(k)));
    try {
      const r = await readerApi.exportNotes();
      if (r.ok) toast.success(`Sent your notes from ${r.count} page${r.count === 1 ? "" : "s"} to ${r.email}.`);
      else toast.error("We could not email your notes just now. Please try again shortly.");
    } catch (e) {
      const msg = e.response?.data?.detail;
      if (e.response?.status === 400) toast.info(typeof msg === "string" ? msg : "Nothing saved to email yet.");
      else { toast.error("Could not email your notes just now."); handleErr(e); }
    } finally { setExporting(false); }
  };

  return (
    <div className={`reader-shell${sideCollapsed ? " is-collapsed" : ""}`} data-testid="reader-app">
      {sideOpen && <div className="reader-backdrop" onClick={() => setSideOpen(false)} data-testid="reader-side-backdrop" />}
      <aside className={`reader-side${sideOpen ? " is-open" : ""}`} data-testid="reader-sidebar">
        <button className="reader-drawer-close" onClick={() => setSideOpen(false)} data-testid="reader-side-close" aria-label="Close contents"><X size={18} /></button>
        <div className="reader-side-head">
          <Link to="/" className="reader-side-logo"><img alt="TransitionWorks" src={images.logoLight || "/tw-logo.png"} /></Link>
          <div className="reader-progress" data-testid="reader-progress">
            <div className="reader-progress-bar"><span style={{ width: `${pct}%` }} /></div>
            <small>{doneCount} of {pages} pages marked read</small>
          </div>
          {sections.length > 0 && (
            <div className="reader-rings" data-testid="reader-section-rings">
              {sections.map((s) => <SectionRing key={s.p} label={s.p} pct={s.pct} onClick={() => goto(s.start)} testId={`reader-ring-${s.p}`} />)}
            </div>
          )}
        </div>
        <div className="reader-side-tools">
          <label className="reader-jump">Go to page
            <input type="number" min={1} max={pages} value={idx + 1} onChange={(e) => goto((parseInt(e.target.value, 10) || 1) - 1)} data-testid="reader-jump" />
          </label>
          <button className={`reader-chip${onlyBookmarks ? " is-on" : ""}`} onClick={() => setOnlyBookmarks((v) => !v)} data-testid="reader-filter-bookmarks"><Star size={13} /> Bookmarks</button>
        </div>

        <nav className="reader-list" data-testid="reader-chapters">
          {onlyBookmarks ? (
            bookmarks.length ? bookmarks.map((i) => (
              <button key={i} className={`reader-list-item${i === idx ? " is-active" : ""}`} onClick={() => goto(i)} data-testid={`reader-page-${i}`}>
                <span className={`reader-tick${progress[String(i)]?.done ? " is-done" : ""}`}>{progress[String(i)]?.done ? <Check size={12} /> : i + 1}</span>
                <span className="reader-list-text"><em>Page {i + 1}</em>Bookmarked</span>
                <Star size={13} className="reader-list-star" />
              </button>
            )) : <p className="reader-empty">No bookmarks yet. Tap the star on any page.</p>
          ) : (
            chapters.map((g, gi) => {
              const isP = ["Purpose", "People", "Program", "Place", "Profit"].includes(g.p);
              const open = openGroup === gi;
              return (
                <div key={g.p} className={`reader-chapter${open ? " is-open" : ""}`} data-testid={`reader-chapter-${gi}`}>
                  <button className="reader-chapter-head" onClick={() => setOpenGroup(open ? -1 : gi)} data-testid={`reader-chapter-head-${gi}`}>
                    {isP && <span className="reader-chapter-dot" />}
                    <span className="reader-chapter-name">{g.p}</span>
                    <span className="reader-chapter-count">{g.items.length}</span>
                    <ChevronDown size={15} className="reader-chapter-caret" />
                  </button>
                  {open && (
                    <div className="reader-chapter-items">
                      {g.items.map((it) => {
                        const st = progress[String(it.page)] || {};
                        return (
                          <button key={it.page} className={`reader-lesson-item${it.page === idx ? " is-active" : ""}`} onClick={() => goto(it.page)} data-testid={`reader-lesson-${it.page}`}>
                            <span className={`reader-tick sm${st.done ? " is-done" : ""}`}>{st.done ? <Check size={11} /> : ""}</span>
                            <span className="reader-lesson-name">{it.title}</span>
                            {st.bookmarked && <Star size={12} className="reader-list-star" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </nav>
      </aside>

      <main className="reader-main" data-testid="reader-main">
        <header className="reader-topbar">
          <button className="reader-menu-btn" onClick={() => setSideOpen(true)} data-testid="reader-menu-btn" aria-label="Contents"><Menu size={18} /></button>
          <button className="reader-collapse-btn" onClick={toggleCollapse} data-testid="reader-collapse-btn" aria-label={sideCollapsed ? "Show contents panel" : "Hide contents panel"} title={sideCollapsed ? "Show contents" : "Hide contents for bigger pages"}>{sideCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}</button>
          <div className="reader-topbar-title"><BookOpen size={16} /> From Campus to Corporate</div>
          <div className="reader-topbar-user">
            <button className={`reader-spread-toggle${spread ? " is-on" : ""}`} onClick={() => setSpread((v) => !v)} data-testid="reader-spread-toggle" title="Two-page view"><Columns2 size={15} /> <span className="reader-btn-label">{spread ? "Single page" : "Two-page"}</span></button>
            <button className={`reader-export reader-panel-toggle${panelOpen ? " is-on" : ""}`} onClick={togglePanel} data-testid="reader-panel-toggle" title={panelOpen ? "Hide action items" : "Show action items"}><ListChecks size={15} /> <span className="reader-btn-label">Action items</span></button>
            <button className="reader-export" onClick={() => setShowTour(true)} data-testid="reader-show-tips" title="Replay the reader tips"><HelpCircle size={14} /> <span className="reader-btn-label">Show tips again</span></button>
            <button className="reader-export" onClick={exportNotes} disabled={exporting} data-testid="reader-export-notes" title="Email me my notes"><Mail size={14} /> <span className="reader-btn-label">{exporting ? "Sending..." : "Email me my notes"}</span></button>
            <span data-testid="reader-user-email">{user.name || user.email}</span>
            <button className="reader-logout" onClick={onSignedOut} data-testid="reader-logout" title="Sign out"><LogOut size={14} /> <span className="reader-btn-label">Sign out</span></button>
          </div>
        </header>

        <div className={`reader-book${panelOpen ? "" : " panel-collapsed"}`} data-testid="reader-book">
          <div className="reader-book-stage">
            <div className="reader-book-actions">
              <button className={`reader-act${ls.bookmarked ? " is-on" : ""}`} onClick={() => update({ bookmarked: !ls.bookmarked }, true)} data-testid="reader-bookmark"><Star size={15} /> {ls.bookmarked ? "Bookmarked" : "Bookmark"}</button>
              <button className={`reader-act${ls.done ? " is-on" : ""}`} onClick={toggleDone} data-testid="reader-done"><Check size={15} /> {ls.done ? "Read" : "Mark read"}</button>
              <button className="reader-act reader-act-notes" onClick={() => setNotesOpen(true)} data-testid="reader-notes-open"><ListChecks size={15} /> Actions</button>
            </div>
            {pages > 0 && (
              <motion.div key={`${idx}-${spread}`} className={`reader-book-view${spread && idx + 1 < pages ? " is-spread" : ""}`} data-testid="reader-book-view"
                initial={{ rotateY: dir * 20, x: dir * 34, opacity: 0 }}
                animate={{ rotateY: 0, x: 0, opacity: 1 }}
                transition={{ duration: 0.42, ease: [0.2, 0.7, 0.2, 1] }}
                style={{ transformOrigin: dir > 0 ? "left center" : "right center" }}>
                <BookPage n={idx} onAuthErr={handleErr} testId="reader-page-img" overlay={overlayFor(idx)} />
                {spread && idx + 1 < pages && <BookPage n={idx + 1} onAuthErr={handleErr} testId="reader-page-img-2" overlay={overlayFor(idx + 1)} />}
              </motion.div>
            )}
            <div className="reader-nav">
              <button disabled={idx === 0} onClick={() => goto(idx - step)} data-testid="reader-prev"><ChevronLeft size={16} /> Previous</button>
              <span data-testid="reader-page-label">{spread && idx + 1 < pages ? `Pages ${idx + 1}\u2013${idx + 2}` : `Page ${idx + 1}`} / {pages}</span>
              <button disabled={idx >= pages - 1} onClick={() => goto(idx + step)} data-testid="reader-next">Next <ChevronRight size={16} /></button>
            </div>
          </div>

          {notesOpen && <div className="reader-backdrop reader-notes-backdrop" onClick={() => setNotesOpen(false)} data-testid="reader-notes-backdrop" />}
          <aside className={`reader-notes-panel${notesOpen ? " is-open" : ""}`} data-testid="reader-notes-panel">
            <div className="reader-notes-head">
              <button className="reader-panel-collapse" onClick={togglePanel} data-testid="reader-panel-collapse" title="Hide action items for a full-width page"><ChevronRight size={14} /> Hide</button>
              <p className="reader-notes-context">Action items · page {idx + 1}</p>
              <button className="reader-notes-close" onClick={() => setNotesOpen(false)} data-testid="reader-notes-close" aria-label="Close"><X size={18} /></button>
            </div>
            <section className="reader-panel">
              <h3>Action items</h3>
              <div className="reader-add">
                <input placeholder="Add an action for yourself" value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()} data-testid="reader-checklist-input" />
                <button onClick={addItem} data-testid="reader-checklist-add"><Plus size={15} /></button>
              </div>
              <ul className="reader-checklist" data-testid="reader-checklist">
                {checklist.map((c) => (
                  <li key={c.id} className={c.checked ? "is-checked" : ""}>
                    <button className="reader-check" onClick={() => toggleItem(c.id)} data-testid={`reader-check-${c.id}`}>{c.checked && <Check size={12} />}</button>
                    <span>{c.text}</span>
                    <button className="reader-del" onClick={() => delItem(c.id)}><Trash2 size={13} /></button>
                  </li>
                ))}
                {checklist.length === 0 && <li className="reader-checklist-empty">No action items on this page yet.</li>}
              </ul>
            </section>
          </aside>
          {!panelOpen && <button className="reader-panel-tab" onClick={togglePanel} data-testid="reader-panel-tab" title="Show action items"><ListChecks size={14} /> Action items</button>}
        </div>
      </main>

      {showTour && <ReaderTour onDone={() => { setShowTour(false); localStorage.setItem(TOUR_KEY, "1"); }} />}

      {celebrate && (
        <div className="reader-celebrate" data-testid="reader-celebrate" onClick={() => setCelebrate(false)}>
          <div className="reader-confetti" aria-hidden="true">{Array.from({ length: 28 }).map((_, i) => <span key={i} style={{ left: `${(i * 3.4) % 100}%`, animationDelay: `${(i % 7) * 0.12}s`, background: i % 2 ? "var(--gold)" : "#F1D98A" }} />)}</div>
          <motion.div className="reader-celebrate-card" initial={{ scale: 0.85, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 220, damping: 18 }} onClick={(e) => e.stopPropagation()}>
            <div className="reader-celebrate-icon"><Trophy size={30} /></div>
            <h2>You finished the book!</h2>
            <p>Every one of the {pages} pages, done. From campus to corporate, you did the work. Come back any time; your notes and reflections stay saved.</p>
            <div className="reader-follow" data-testid="celebrate-follow">
              <span className="reader-follow-title">Follow Scott for more lessons</span>
              <div className="reader-follow-links">
                <a href={links?.linkedin} target="_blank" rel="noopener noreferrer" className="reader-follow-btn" data-testid="celebrate-follow-linkedin"><LinkedInIcon /> LinkedIn</a>
                <a href={links?.tiktok} target="_blank" rel="noopener noreferrer" className="reader-follow-btn" data-testid="celebrate-follow-tiktok"><TikTokIcon /> TikTok</a>
              </div>
            </div>
            <div className="reader-celebrate-actions">
              <button className="btn btn-gold" onClick={goReview} data-testid="celebrate-review">Leave a 5-star review</button>
              <button className="btn btn-light" onClick={getBadge} data-testid="celebrate-badge">Get my badge</button>
              <button className="btn btn-light reader-linkedin-btn" onClick={shareLinkedIn} data-testid="celebrate-linkedin"><Linkedin size={15} /> Share on LinkedIn</button>
              <button className="btn btn-light" onClick={() => { setCelebrate(false); exportNotes(); }} data-testid="celebrate-email-notes">Email me my notes</button>
              <button className="reader-celebrate-dismiss" onClick={() => setCelebrate(false)} data-testid="celebrate-close">Keep reading</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

/* ---------------- First-login password change ---------------- */
function ChangePassword({ user, onDone }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return setErr("Please choose a password of at least 6 characters.");
    if (pw !== pw2) return setErr("Those passwords do not match.");
    setErr(""); setBusy(true);
    try { await readerApi.changePassword(pw); toast.success("Password set. Enjoy your eBook!"); onDone(); }
    catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="reader-auth" data-testid="reader-change-password">
      <div className="reader-auth-card">
        <Link to="/" className="reader-auth-logo"><img alt="TransitionWorks" src="/tw-logo.png" /></Link>
        <span className="eyebrow">Welcome{user.name ? `, ${user.name.split(" ")[0]}` : ""}</span>
        <h1>Set your password</h1>
        <p className="reader-auth-note">Your account ({user.email}) was created with your purchase. Choose a password you'll use from now on.</p>
        <form onSubmit={submit} className="reader-auth-form">
          <input type="password" required placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="change-pw-input" />
          <input type="password" required placeholder="Confirm password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="change-pw-confirm" />
          {err && <div className="reader-auth-err" data-testid="change-pw-error">{err}</div>}
          <button className="btn btn-gold btn-block" disabled={busy} type="submit" data-testid="change-pw-submit">{busy ? "Saving..." : "Save & start reading"}</button>
        </form>
      </div>
    </div>
  );
}

/* ---------------- Route entry ---------------- */
export default function Reader() {
  const [params] = useSearchParams();
  const resetToken = params.get("reset");
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (resetToken) { setUser(false); return; }
    if (!readerToken.get()) { setUser(false); return; }
    readerApi.me().then(setUser).catch(() => { readerToken.clear(); setUser(false); });
  }, [resetToken]);

  const signOut = async () => { await readerApi.logout(); setUser(false); };

  if (user === null) return <div className="site-splash" data-testid="reader-splash" />;
  if (!user) return <ReaderAuth resetToken={resetToken} onAuthed={setUser} />;
  if (user.mustChange) return <ChangePassword user={user} onDone={() => setUser({ ...user, mustChange: false })} />;
  return <ReaderApp user={user} onSignedOut={signOut} />;
}
